// La section « Assignés » du détail d'un ticket : chips en lecture + un bouton « Modifier »
// (gardé par feature:tickets:write, même patron GatedButton que la suppression) qui déplie le
// même sélecteur de membres que la modale de création (AssigneePicker partagé : recherche +
// chips retirables). L'enregistrement passe par le PUT assignees existant du ticketsClient.
import React, { useMemo, useState } from "react";
import { FiCheck, FiEdit2, FiUsers, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { AssigneePicker } from "../../Components/Tickets/AssigneePicker";
import { getUserDirectory, type DirectoryEntry } from "../../Components/Account/accountClient";
import GatedButton from "../../Components/GatedButton";
import { AuthorBadge, SectionLabel } from "./TicketBits";
import { ticketsClient, type Ticket, type TicketAuthor } from "./ticketsClient";

export const AssigneeEditor: React.FC<{
	ticket: Ticket;
	/** Rechargement du détail (et de la liste) après un enregistrement réussi. */
	onSaved: () => void;
}> = ({ ticket, onSaved }) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState<string[]>([]);
	const [directory, setDirectory] = useState<DirectoryEntry[]>([]);
	const [busy, setBusy] = useState(false);

	const options = useMemo(
		() => directory.filter((d) => !!d.email).map((d) => ({ email: d.email as string, name: d.name || (d.email as string) })),
		[directory]
	);

	const openEditor = () => {
		setDraft([...ticket.assignees]);
		setEditing(true);
		// Best-effort : sans annuaire, le panneau reste utilisable (retrait de chips).
		getUserDirectory().then(setDirectory).catch(() => setDirectory([]));
	};

	const save = async () => {
		setBusy(true);
		try {
			await ticketsClient.setAssignees(ticket.id, draft);
			toast.success("Assignés mis à jour.");
			setEditing(false);
			onSaved();
		} catch {
			toast.error("Échec de la mise à jour des assignés.");
		} finally {
			setBusy(false);
		}
	};

	const chips = (ticket.assigneesResolved && ticket.assigneesResolved.length > 0
		? ticket.assigneesResolved.map((a) => ({ key: a.id ?? a.email ?? a.name, author: a as TicketAuthor | null, fallback: a.email }))
		: ticket.assignees.map((e) => ({ key: e, author: null as TicketAuthor | null, fallback: e }))
	);

	return (
		<div>
			<SectionLabel
				icon={<FiUsers className="h-3 w-3" />}
				trailing={
					!editing && (
						<GatedButton
							token="feature:tickets:write"
							onClick={openEditor}
							title="Modifier les assignés"
							className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
						>
							<FiEdit2 className="h-3 w-3" /> Modifier
						</GatedButton>
					)
				}
			>
				Assignés
			</SectionLabel>

			{editing ? (
				<div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-sm">
					<AssigneePicker value={draft} onChange={setDraft} options={options} placeholder="Rechercher un membre…" />
					{draft.length === 0 && <p className="mt-2 text-[11px] text-slate-400">Aucun assigné : le ticket restera non assigné.</p>}
					<div className="mt-3 flex justify-end gap-2 border-t border-slate-200/70 pt-2.5">
						<button
							type="button"
							onClick={() => setEditing(false)}
							disabled={busy}
							className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
						>
							<FiX className="h-3.5 w-3.5" /> Annuler
						</button>
						<button
							type="button"
							onClick={save}
							disabled={busy}
							className="inline-flex items-center gap-1.5 rounded-full bg-[#005CA9] px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#004a87] disabled:cursor-not-allowed disabled:opacity-50"
						>
							<FiCheck className="h-3.5 w-3.5" /> {busy ? "Enregistrement…" : "Enregistrer"}
						</button>
					</div>
				</div>
			) : chips.length === 0 ? (
				<p className="text-sm text-slate-400">Non assigné.</p>
			) : (
				<div className="flex flex-wrap gap-2">
					{chips.map((a) => (
						<span key={a.key} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600 shadow-sm">
							<AuthorBadge author={a.author} fallback={a.fallback} />
						</span>
					))}
				</div>
			)}
		</div>
	);
};

export default AssigneeEditor;
